import { Fragment, useEffect, useState, type ReactElement } from 'react';
import {
  AUDIT_CATEGORIES,
  api,
  type AuditCategory,
  type AuditEntry,
  type AuditFilters,
} from '../shared/api/endpoints';
import { useLocale } from '../shared/i18n/locale';
import { actorLabel, describeAudit, objectLabel } from '../shared/lib/audit-text';
import { useReasonNames } from '../shared/lib/use-reason-names';
import { formatTime } from '../shared/lib/format';
import {
  AUDIT_PRESETS,
  periodFilters,
  presetOf,
  presetPeriod,
  sameDay,
  startOfDay,
  addDays,
  type Period,
} from '../shared/lib/period';
import { useAsync } from '../shared/lib/use-async';
import { PeriodPicker } from '../shared/ui/PeriodPicker';
import { EmptyState, FilterChips, secondaryButtonStyle } from '../shared/ui/primitives';
import { OrderDrawer } from './OrderDrawer';

/**
 * Журнал аудита (ТЗ разд. 13, FR-521).
 *
 * Читается человеком, а не разработчиком: «12:00 · Рустам Ахмедов, Логист · Назначен
 * курьер · Заказ A-000885 · Жасур Нурматов». Идентификаторов на экране нет нигде —
 * объект назван по имени и открывается щелчком. Сырые значения — под «технические
 * данные», и только у администратора: логисту они ничего не скажут.
 *
 * Строки сгруппированы по дням, одинаковые события подряд свёрнуты сервером в «×N».
 * Подгрузка — кнопкой «Показать ещё» по `audit_page_size` записей: журнал активной
 * инсталляции за месяц — сотни тысяч строк.
 */

const SEARCH_DEBOUNCE_MS = 350;

type Who = '' | 'ct' | 'system' | `user:${string}`;

export function AuditPage({ canSeeRaw }: { readonly canSeeRaw: boolean }): ReactElement {
  const { locale, messages } = useLocale();
  const labels = messages.cabinet.journal;

  const [period, setPeriod] = useState<Period | null>(() => presetPeriod('today', new Date()));
  const [category, setCategory] = useState<AuditCategory | 'all'>('all');
  const [who, setWho] = useState<Who>('');
  const [typed, setTyped] = useState('');
  const [order, setOrder] = useState('');
  const [extra, setExtra] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setOrder(typed.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [typed]);

  const filters: AuditFilters = {
    ...periodRange(period),
    ...(category === 'all' ? {} : { category }),
    ...(who === 'ct' || who === 'system' ? { actor_role: who } : {}),
    ...(who.startsWith('user:') ? { actor_id: who.slice('user:'.length) } : {}),
    ...(order === '' ? {} : { order }),
  };
  const key = JSON.stringify(filters);

  useEffect(() => {
    setExtra([]);
    setCursor(null);
  }, [key]);

  const facets = useAsync(() => api.auditFacets(), []);
  const page = useAsync(() => api.auditJournal(filters), [key]);

  const rows = [...(page.data?.data ?? []), ...extra];
  const next = cursor ?? page.data?.next_cursor ?? null;
  const now = new Date();
  const preset = presetOf(period, now, AUDIT_PRESETS);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <h1>{labels.title}</h1>
        <span className="caption">
          {`${labels.total} `}
          <b className="num" style={{ color: 'var(--text-2)' }}>
            {page.data?.total ?? rows.length}
          </b>
        </span>
      </header>

      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterChips
          active={preset ?? ''}
          onSelect={(code) => setPeriod(presetPeriod(code as 'today', new Date()))}
          items={AUDIT_PRESETS.map((code) => ({
            code,
            label: code === 'last7' ? labels.last7 : labels[code as 'today' | 'yesterday'],
          }))}
        />
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          presets={AUDIT_PRESETS}
          emptyLabel={labels.pickDates}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterChips
          active={category}
          onSelect={(code) => setCategory(code as AuditCategory | 'all')}
          items={(['all', ...AUDIT_CATEGORIES] as const).map((code) => ({
            code,
            label: labels.categories[code],
          }))}
        />

        <select
          aria-label={labels.whoLabel}
          value={who}
          onChange={(event) => setWho(event.target.value as Who)}
          style={{ height: 'var(--field-h)', minWidth: '200px' }}
        >
          <option value="">{`${labels.whoLabel}: ${labels.whoAny}`}</option>
          <option value="ct">{labels.actors.ct}</option>
          <option value="system">{labels.actors.system}</option>
          {(facets.data?.actors ?? []).map((actor) => (
            <option key={actor.id} value={`user:${actor.id}`}>
              {actor.name}
            </option>
          ))}
        </select>

        <input
          type="search"
          value={typed}
          placeholder={labels.orderSearch}
          aria-label={labels.orderSearch}
          onChange={(event) => setTyped(event.target.value)}
          style={{ height: 'var(--field-h)', width: '200px' }}
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState text={page.loading ? messages.cabinet.common.loading : labels.empty} />
      ) : (
        <section
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--shadow-card)',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr className="caption" style={{ textAlign: 'left', textTransform: 'uppercase' }}>
                <th style={cell}>{labels.when}</th>
                <th style={cell}>{labels.who}</th>
                <th style={cell}>{labels.event}</th>
                <th style={cell}>{labels.object}</th>
                <th style={cell}>{labels.details}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((entry, index) => {
                const day = startOfDay(new Date(entry.created_at));
                const previous = rows[index - 1];
                const newDay =
                  previous === undefined || !sameDay(day, new Date(previous.created_at));

                return (
                  <Fragment key={entry.id}>
                    {newDay ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="caption"
                          style={{
                            ...cell,
                            background: 'var(--bg)',
                            fontWeight: 'var(--fw-semibold)',
                          }}
                        >
                          {dayLabel(day, now, messages)}
                        </td>
                      </tr>
                    ) : null}
                    <AuditRow
                      entry={entry}
                      canSeeRaw={canSeeRaw}
                      onOpenOrder={setOpened}
                      locale={locale}
                    />
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          {next === null ? null : (
            <div style={{ padding: 'var(--sp-3) var(--sp-4)', borderTop: '1px solid var(--line)' }}>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={() => {
                  void api.auditJournal({ ...filters, cursor: next }).then((more) => {
                    setExtra((current) => [...current, ...more.data]);
                    setCursor(more.next_cursor ?? null);
                  });
                }}
              >
                {labels.more}
              </button>
            </div>
          )}
        </section>
      )}

      {opened === null ? null : (
        <OrderDrawer
          orderId={opened}
          onClose={() => setOpened(null)}
          onChanged={() => page.reload()}
        />
      )}
    </div>
  );
}

const cell = {
  padding: 'var(--sp-2) var(--sp-4)',
  verticalAlign: 'top',
} as const;

function AuditRow({
  entry,
  canSeeRaw,
  onOpenOrder,
  locale,
}: {
  readonly entry: AuditEntry;
  readonly canSeeRaw: boolean;
  readonly onOpenOrder: (orderId: string) => void;
  readonly locale: ReturnType<typeof useLocale>['locale'];
}): ReactElement {
  const { messages } = useLocale();
  const reasonNames = useReasonNames(locale);
  const labels = messages.cabinet.journal;
  const [raw, setRaw] = useState(false);
  const text = describeAudit(entry, messages, locale, reasonNames);
  const actor = actorLabel(entry, messages);
  const object = objectLabel(entry, messages);
  // Заказ без имени уже удалён (приёмочные данные): карточка не откроется, ссылки нет.
  const orderId = entry.object?.label === null ? null : (entry.object?.order_id ?? null);
  const showRawToggle = canSeeRaw;

  return (
    <tr style={{ borderTop: '1px solid var(--line)' }}>
      <td className="num" style={{ ...cell, whiteSpace: 'nowrap' }}>
        {formatTime(entry.created_at)}
      </td>
      <td style={cell}>
        <b style={{ fontWeight: 'var(--fw-semibold)' }}>{actor.name}</b>
        {actor.role === null ? null : <span className="sub">{actor.role}</span>}
      </td>
      <td style={cell}>
        {text.event}
        {(entry.repeat ?? 1) > 1 ? (
          <span
            className="num"
            style={{
              marginLeft: 'var(--sp-2)',
              color: 'var(--text-muted)',
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            {`×${String(entry.repeat)}`}
          </span>
        ) : null}
      </td>
      <td style={cell}>
        {orderId !== null && object !== '' ? (
          <button
            type="button"
            onClick={() => onOpenOrder(orderId)}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              color: 'var(--accent)',
              fontSize: 'var(--fs)',
              textAlign: 'left',
            }}
          >
            {object}
          </button>
        ) : (
          object
        )}
      </td>
      <td style={cell}>
        {text.details}
        {showRawToggle ? (
          <>
            {' '}
            <button
              type="button"
              onClick={() => setRaw((value) => !value)}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                color: 'var(--text-muted)',
                fontSize: 'var(--fs-sm)',
                textDecoration: 'underline',
              }}
            >
              {raw ? labels.techHide : labels.tech}
            </button>
            {raw ? (
              <pre
                className="num"
                style={{
                  margin: 'var(--sp-2) 0 0',
                  padding: 'var(--sp-2)',
                  background: 'var(--bg)',
                  borderRadius: 'var(--r-sm)',
                  fontSize: 'var(--fs-xs)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {JSON.stringify({ before: entry.old_value, after: entry.new_value }, null, 2)}
              </pre>
            ) : null}
          </>
        ) : null}
      </td>
    </tr>
  );
}

/** Период — в полуинтервал для сервера: `to` не включительно (Р-021). */
function periodRange(period: Period | null): Pick<AuditFilters, 'from' | 'to'> {
  const range = periodFilters(period);

  return {
    ...(range.created_from === undefined ? {} : { from: range.created_from }),
    ...(range.created_to === undefined ? {} : { to: range.created_to }),
  };
}

/** «Сегодня», «Вчера», «31 октября». */
export function dayLabel(
  day: Date,
  now: Date,
  messages: ReturnType<typeof useLocale>['messages'],
): string {
  const labels = messages.cabinet.period;
  const today = startOfDay(now);

  if (sameDay(day, today)) {
    return labels.today;
  }

  if (sameDay(day, addDays(today, -1))) {
    return labels.yesterday;
  }

  return labels.dayMonth
    .replace('{day}', String(day.getDate()))
    .replace('{month}', labels.monthsOf[day.getMonth()] ?? '');
}
